import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Circle, FeatureGroup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Button, Table } from 'reactstrap';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { FaCheck, FaFileImport, FaCity } from 'react-icons/fa';
import CSVReader from 'react-csv-reader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CitySelector from './CitySelector';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
});

// Custom component to recenter map based on userLocation
const RecenterMap = ({ userLocation }) => {
  const map = useMap();
  useEffect(() => {
    if (userLocation) {
      map.setView(userLocation, 10); 
    }
  }, [userLocation, map]);
  return null;
};

const MapController = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => [loc.lat, loc.lon]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);

  return null;
};

const LocationPicker = ({ locations, setLocations, toggleMap }) => {
  const MAX_RADIUS = 10000;
  const [userLocation, setUserLocation] = useState([41.0082, 28.9784]);
  const [showCitySelector, setShowCitySelector] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
        },
        (error) => {
          console.error("Error getting user location:", error);
          toast.error("Unable to get your location, defaulting to Istanbul.");
        }
      );
    } else {
      toast.error("Geolocation is not supported by this browser.");
    }
  }, []);

  const onCreated = (e) => {
    const layer = e.layer;
    const { lat, lng } = layer.getLatLng();
    const radius = layer.getRadius();

    if (radius > MAX_RADIUS) {
      toast.error(`Radius cannot be larger than 10,000 meters!`);
      layer.remove();
      return;
    }

    const newLocation = { lat, lon: lng, meter: radius };
    setLocations((prevLocations) => [...prevLocations, newLocation]);
  };

  const onDeleted = (e) => {
    const layers = e.layers;
    const newLocations = [];
    layers.eachLayer((layer) => {
      const { lat, lng } = layer.getLatLng();
      const radius = layer.getRadius();
      const layerLocation = { lat, lon: lng, meter: radius };
      newLocations.push(layerLocation);
    });
    setLocations((prevLocations) =>
      prevLocations.filter((loc) => !newLocations.some((layerLoc) => layerLoc.lat === loc.lat && layerLoc.lon === loc.lon && layerLoc.meter === loc.meter))
    );
  };

  const handleCSVImport = (data) => {
    const importedLocations = data.map(row => {
      const lat = parseFloat(row[0]);
      const lon = parseFloat(row[1]);
      const meter = parseFloat(row[2]);
      if (isNaN(lat) || isNaN(lon) || isNaN(meter)) {
        console.error(`Invalid data: ${row}`);
        return null;
      }
      if (meter > MAX_RADIUS) {
        toast.error(`CSV dosyasındaki bir radius 10.000 metreden büyük olamaz: ${row}`);
        return null;
      }
      return { lat, lon, meter };
    }).filter(loc => loc !== null);

    setLocations((prevLocations) => [...prevLocations, ...importedLocations]);
  };

  const handleCitySelect = (cityLocation) => {
    if (cityLocation) {
      setLocations(prevLocations => [...prevLocations, cityLocation]);
      // Center map on the selected city
      setUserLocation([cityLocation.lat, cityLocation.lon]);
    }
  };

  return (
    <div style={{ position: "fixed", top: "15%", left: "50%", transform: "translate(-50%, -20%)", width: "80vw", height: "80vh", zIndex: 1000, backgroundColor: "rgba(255, 255, 255, 1)" }}>
      <ToastContainer />
      <div style={{ textAlign: "center", padding: "10px" }}>
        <h3>Select Location</h3>
      </div>
      <MapContainer center={userLocation} zoom={8} style={{ height: "70%", width: "100%" }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController locations={locations} />
        <FeatureGroup>
          <EditControl
            position="topleft"
            onCreated={onCreated}
            onDeleted={onDeleted}
            draw={{
              rectangle: false,
              polygon: false,
              polyline: false,
              circlemarker: false,
              marker: false,
              circle: true
            }}
          />
          {locations.map((loc, index) => (
            <Circle 
              key={index} 
              center={[loc.lat, loc.lon]} 
              radius={loc.meter} 
              pathOptions={{ 
                fillColor: loc.display_name ? '#4CAF50' : '#2196F3',
                color: loc.display_name ? '#388E3C' : '#1976D2',
                weight: 2,
                fillOpacity: 0.3
              }} 
            >
            </Circle>
          ))}
        </FeatureGroup>
      </MapContainer>

      <div style={{ padding: "10px", overflowY: "auto", maxHeight: "20%" ,maxWidth:"79%"}}>
        <Table bordered>
          <thead>
            <tr>
              <th>#</th>
              <th>Location</th>
              <th>Latitude</th>
              <th>Longitude</th>
              <th>Radius (meters)</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{loc.display_name || 'Custom Location'}</td>
                <td>{loc.lat}</td>
                <td>{loc.lon}</td>
                <td>{loc.meter}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div style={{ position: "absolute", bottom: "10px", right: "10px", zIndex: 1001 }}>
        <div style={{ marginBottom: "10px", display: "flex", justifyContent: "flex-end" }}>
          <Button 
            color="info" 
            onClick={() => setShowCitySelector(true)}
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
              height: '38px',
              width: '224px',
              fontSize: '0.9rem'
            }}
          >
            <FaCity /> Select by City
          </Button>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <CSVReader
            cssClass="csv-reader-input"
            label=""
            onFileLoaded={handleCSVImport}
            inputId="csv-input"
            inputStyle={{ display: 'none' }}
          />
          <label htmlFor="csv-input" className="btn btn-primary" style={{ height: '38px', width: '107px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaFileImport /> Import CSV
          </label>
          <Button 
            color="primary" 
            onClick={toggleMap}
            style={{ 
              height: '38px',
              width: '107px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FaCheck /> Done
          </Button>
        </div>
      </div>

      <CitySelector
        isOpen={showCitySelector}
        toggle={() => setShowCitySelector(false)}
        onCitySelect={handleCitySelect}
      />
    </div>
  );
};

export default LocationPicker;
